/**
 * Tenant-timezone-aware date formatting utilities.
 *
 * All dates from the backend are stored as UTC ISO strings.
 * Display uses the tenant's timezone obtained from useAuth().
 *
 * Uses date-fns-tz for reliable timezone handling in the browser.
 */

import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

/**
 * Format a UTC ISO date string to the given tenant timezone.
 *
 * @param utcIso    - ISO datetime from the backend (always UTC).
 * @param timezone  - IANA timezone string, e.g. "Asia/Kolkata".
 * @param fmt       - date-fns format string; defaults to 'PP' (e.g. "May 12, 2026").
 *
 * @example
 *   formatDateTz('2026-05-12T07:30:00Z', 'Asia/Kolkata')
 *   // → "May 12, 2026"
 */
export function formatDateTz(
  utcIso: string | null | undefined,
  timezone: string,
  fmt = 'PP',
): string {
  if (!utcIso) return '—';
  try {
    const zonedDate = toZonedTime(new Date(utcIso), timezone);
    return format(zonedDate, fmt);
  } catch (err) {
    // Surface the failure so QA notices instead of silently rendering raw UTC.
    console.warn('[formatDateTz] failed to format', { utcIso, timezone, err });
    return '—';
  }
}

/**
 * Format a UTC ISO datetime with time component.
 *
 * @example
 *   formatDateTimeTz('2026-05-12T07:30:00Z', 'Asia/Kolkata')
 *   // → "May 12, 2026, 1:00 PM"
 */
export function formatDateTimeTz(
  utcIso: string | null | undefined,
  timezone: string,
  fmt = 'PPp',
): string {
  return formatDateTz(utcIso, timezone, fmt);
}

/**
 * React hook wrapper: returns a pre-bound formatDateTz using the current
 * user's tenant timezone from AuthContext.
 *
 * Usage: const fmt = useTenantDateFormatter(); fmt('2026-05-12T00:00:00Z')
 */
import { useAuth } from '@/context';

export function useTenantDateFormatter(
  fmt = 'PP',
): (utcIso: string | null | undefined) => string {
  const { user } = useAuth();
  const tz = user?.tenant_timezone ?? 'UTC';
  return (utcIso) => formatDateTz(utcIso, tz, fmt);
}

